import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
    Row,
    Col,
    Card,
    Table,
    notification,
    Modal,
    Button,
    Image,
    Pagination,
    Input,
    Carousel,
    Tooltip,
    Typography
} from "antd";
import apiClient from "../../services/api";
import { useLocation } from 'react-router-dom';
import { DeleteOutlined, EditOutlined, ExclamationCircleOutlined, EyeOutlined } from "@ant-design/icons";
import { baseUrl } from "../../config";
import { skeletonData } from "../../components/skeletons/Blog";
import moment from "moment";
import { handleApiError, handleApiSuccess, handleApiInfo } from "../../utils/errorHandler";
import { createSafeHTML } from "../../utils/sanitize";
import logger from "../../utils/logger";

const { Search } = Input;
const { confirm } = Modal;
const { Title } = Typography;

// table code start
const columns = [
    {
        title: "#",
        dataIndex: "index",
        key: "index"
    },
    {
        title: "Title",
        dataIndex: "title",
        key: "title"
    },
    {
        title: "Slug",
        dataIndex: "slug",
        key: "slug"
    },
    // {
    //     title: "Type",
    //     dataIndex: "type",
    //     key: "type"
    // },
    // {
    //     title: "Images",
    //     dataIndex: "images",
    //     key: "images"
    // },
    // {
    //     title: "Short Content",
    //     dataIndex: "shortContent",
    //     key: "shortContent"
    // },
    {
        title: "Body Content",
        dataIndex: "content",
        key: "content"
    },
    {
        title: "Created By",
        dataIndex: "createdByName",
        key: "createdByName"
    },
    {
        title: "Created At",
        dataIndex: "createdAt",
        key: "createdAt"
    }
    // {
    //     title: "Action",
    //     key: "action",
    //     dataIndex: "action",
    // }
];

function QuestionList() {
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const userId = searchParams.get('user');
    const [userList, setUserList] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalUsers, setTotalUsers] = useState(0);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [modalVisible, setModalVisible] = useState(false);
    const [privacy, setPrivacy] = useState('');
    const [titleName, setTitleName] = useState();
    const [imageModalVisible, setImageModalVisible] = useState(false);
    const [selectedImages, setSelectedImages] = useState([]);

    useEffect(() => {
        getList()
    }, [searchTerm, currentPage, pageSize, userId]) // eslint-disable-line react-hooks/exhaustive-deps

    async function getList() {
        try {
            setLoading(true);

            const params = {
                page: currentPage,
                items: pageSize,
            };

            if (userId) {
                params.createdBy = userId;
            }

            // Check if search is not null or empty, then add it to params
            if (searchTerm && searchTerm.trim() !== '') {
                params.q = searchTerm;
                params.fields = 'title,createdBy';
                // params.fields = 'name,email'; // Specify searchable fields
            }

            const response = await apiClient.get('/api/question-admin', { params });

            if (response.data.success) {
                setUserList(response.data.result);
                setTotalUsers(response.data.pagination.count);
            } else {
                setUserList([]);
            }
        } catch (error) {
            handleApiError(error);
            logger.error("API error:", error);
            setUserList([]);
        } finally {
            setLoading(false);
        }
    };

    async function handleDelete(id) {
        try {
            const response = await apiClient.delete(`/api/question/${id}`);
            if (response.status === 200) {
                getList();
                handleApiSuccess('Question deleted successfully!');
            } else {
                handleApiInfo(response.data.message);
            }
        } catch (error) {
            handleApiError(error);
            logger.error("API error:", error);
        }
    };

    const showModal = (privacy, name) => {
        setTitleName(name);
        setPrivacy(privacy || '')
        setModalVisible(true);
    };

    const handleOk = () => {
        setModalVisible(false);
    };

    const handleCancel = () => {
        setModalVisible(false);
    };

    const handlePageChange = (page, pageSize) => {
        setCurrentPage(page);
        setPageSize(pageSize);
    };

    const handleSearch = (value) => {
        if (value.length >= 3 || value.length === 0) {
            setSearchTerm(value);
            setCurrentPage(1);
        }
    };

    const showImageModal = (images) => {
        setSelectedImages(images);
        setImageModalVisible(true);
    };

    return (
        <>
            <div className="tabled">
                <Row gutter={[24, 0]}>
                    <Col xs="24" xl={24}>
                        <Card
                            bordered={false}
                            className="criclebox tablespace mb-24"
                            title="Question Management"
                            extra={
                                <>
                                    <div style={{ display: "flex", flexWrap: "wrap", gap: "10px", justifyContent: "space-between" }}>
                                        <Search
                                            placeholder="Search by title, created by"
                                            onSearch={handleSearch}
                                            allowClear
                                            style={{ flex: 1, minWidth: "400px" }} // Makes search bar flexible
                                        />
                                        {/* <Link className="custom-btn" to="/blog-management/add">Add</Link> */}
                                    </div>
                                </>
                            }
                        >
                            <div className="table-responsive">
                                <Table
                                    columns={columns}
                                    dataSource={loading ? skeletonData : userList.map((user, index) => ({
                                        index: (currentPage - 1) * pageSize + index + 1,
                                        title: (<Tooltip title={user.title}>
                                            <span>
                                                {user.title?.split(' ').slice(0, 3).join(' ')}
                                                {user.title?.split(' ').length > 3 ? '...' : ''}
                                            </span>
                                        </Tooltip>),
                                        createdByName: (
                                            <Link to={`/users/${user.createdBy._id}`}>
                                                <div className="avatar-info">
                                                    <Title level={5}>{user?.createdBy?.firstName || ''} {user?.createdBy?.lastName || ''}</Title>
                                                    <p style={{ "text-decoration": "underline" }}>{user.createdBy.email}</p>
                                                </div>
                                            </Link>

                                        ),
                                        // images: Array.isArray(user.images) && user.images.length > 0 ? (
                                        //     <div onClick={() => showImageModal(user.images)} style={{ cursor: 'pointer' }}>
                                        //         <Carousel autoplay dots={false} style={{ width: 80 }}>
                                        //             {user.images.map((img, i) => (
                                        //                 <div key={i}>
                                        //                     <Image
                                        //                         src={`${baseUrl}/${img}`}
                                        //                         width={60}
                                        //                         height={60}
                                        //                         style={{ objectFit: 'cover' }}
                                        //                         preview={false}
                                        //                     />
                                        //                 </div>
                                        //             ))}
                                        //         </Carousel>
                                        //     </div>
                                        // ) : '-',
                                        slug: (<Tooltip title={user.slug}>
                                            <span>
                                                {user.slug?.split('-').slice(0, 3).join('-')}
                                                {user.slug?.split('-').length > 3 ? '...' : ''}
                                            </span>
                                        </Tooltip>),
                                        // type: user.type,
                                        // shortContent: (
                                        //     <Button type="primary" onClick={() => showModal(user.shortContent, 'Short Content')}>
                                        //         <EyeOutlined />
                                        //     </Button>
                                        // ),
                                        content: (
                                            <Button type="primary" onClick={() => showModal(user.bodyContent, 'Content')}>
                                                <EyeOutlined />
                                            </Button>
                                        ),
                                        createdAt: (
                                            <p>{moment(user.createdAt).format("MMMM DD, YYYY hh:mm A")}</p>
                                        ),
                                    }))}
                                    pagination={false}
                                    className="ant-border-space"
                                />
                            </div>
                            {/* Keep pagination outside the scrollable div */}
                            <div className="pagination-container">
                                <Pagination
                                    current={currentPage}
                                    pageSize={pageSize}
                                    total={totalUsers}
                                    onChange={handlePageChange}
                                />
                            </div>
                        </Card>
                    </Col>
                </Row>
            </div>
            <Modal
                title={`${titleName}`}
                visible={modalVisible}
                onOk={handleOk}
                onCancel={handleCancel}
            >
                <div className="author-info">
                    <div dangerouslySetInnerHTML={{ __html: createSafeHTML(privacy) }} />
                </div>
            </Modal>
            <Modal
                title="Images"
                visible={imageModalVisible}
                onCancel={() => setImageModalVisible(false)}
                footer={null}
                width={600}
            >
                <Carousel autoplay>
                    {selectedImages.map((img, i) => (
                        <div key={i} style={{ textAlign: 'center' }}>
                            <Image
                                src={`${baseUrl}/${img}`}
                                alt={`image-${i}`}
                                style={{ maxHeight: '400px', maxWidth: '100%', objectFit: 'contain' }}
                            />
                        </div>
                    ))}
                </Carousel>
            </Modal>

        </>
    );
}

export default QuestionList;
